/*
 * QoSPanel — "Connection quality rules" in plain language.
 *
 * Each traffic class gets a simple quality LEVEL (Real-time, Responsive,
 * Streaming, Reliable, Standard). Picking a level applies a backend preset.
 * Power users can still open "Advanced" to tune raw thresholds.
 *
 * API endpoints:
 *   GET    /qos/thresholds        — configured per-class quality thresholds
 *   PUT    /qos/thresholds        — set/update a class threshold (steers routing)
 *   DELETE /qos/thresholds?class= — remove a threshold (revert to category)
 *   GET    /qos/presets           — preset templates + category defaults
 *   GET    /qos/status            — live per-WAN quality vs each class
 *   GET    /qos/health            — global link-failover thresholds
 *   PUT    /qos/health            — set global link-failover thresholds
 *   GET    /qos/events            — recent failover activity (WAN degrade/recover)
 *
 * Two tiers, both live on the edge:
 *   1. Global failover (/qos/health) — when ANY link is abandoned for everyone.
 *   2. Per-class quality (/qos/thresholds) — an extra, stricter bar; a WAN that
 *      fails a class's quality ceiling is pushed to the back of THAT class's
 *      failover order, even while it's still fine for everything else.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  FiShield, FiZap, FiActivity, FiWifi, FiCheck, FiX,
  FiAlertTriangle, FiSearch, FiRefreshCw, FiClock,
  FiSliders, FiSave, FiInfo, FiEye, FiEyeOff,
  FiChevronDown,
} from "react-icons/fi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import useWheel from "../hooks/useWheel";
import { useWanName, WanLabel } from "./WanNames";
import { useConfirm } from "./ConfirmModal";

/* ═══════════════════════════════════════════════════════════════════════════
   LEVELS — plain language wrappers over the backend preset names.
   The KEYS must match backend preset names (used by applyPreset / inferLane).
   ═══════════════════════════════════════════════════════════════════════════ */

const LEVEL_ORDER = [
  "ultra-low-latency", "low-latency", "high-bandwidth", "reliable", "best-effort",
];

const LEVEL_META = {
  "ultra-low-latency": {
    icon: FiZap, color: "#dc2626", label: "Real-time",
    short: "Calls, gaming, live control",
    long: "Moves to a better connection as soon as lag, jitter or loss passes its limit.",
  },
  "low-latency": {
    icon: FiActivity, color: "#f59e0b", label: "Responsive",
    short: "Video meetings, conferencing",
    long: "Keeps delay low so conversations stay smooth.",
  },
  "high-bandwidth": {
    icon: FiWifi, color: "#3b82f6", label: "Streaming",
    short: "Video, large downloads",
    long: "Prefers the fastest connection for heavy throughput.",
  },
  reliable: {
    icon: FiShield, color: "#10b981", label: "Reliable",
    short: "Email, web, work apps",
    long: "Steers away from flaky links to avoid interruptions.",
  },
  "best-effort": {
    // A colour of its own: light grey read as switched off, and every level
    // can be picked.
    icon: FiCheck, color: "#7c3aed", label: "Standard",
    short: "No special handling",
    long: "Uses whatever connection is currently active. The default.",
  },
};

/* ═══════════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

function scoreColor(v) {
  if (v >= 80) return "#22c55e";
  if (v >= 60) return "#84cc16";
  if (v >= 40) return "#eab308";
  if (v >= 20) return "#f97316";
  return "#ef4444";
}

function friendlyClass(key) {
  return key.replace(/^APP_/, "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function inferLevel(row) {
  if (row.preset && LEVEL_ORDER.includes(row.preset)) return row.preset;
  if (!row.enabled) return "best-effort";
  if (row.profile === "low-latency" && row.min_score >= 65) return "ultra-low-latency";
  if (row.profile === "low-latency") return "low-latency";
  if (row.profile === "high-bw") return "high-bandwidth";
  if (row.min_score >= 40) return "reliable";
  return "best-effort";
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

export default function QoSPanel({ selectedNode, nodeApiUrl }) {
  const wanName = useWanName();
  /* ── Data ── */
  const [thresholds, setThresholds] = useState([]);
  const [presets, setPresets] = useState([]);
  const [categoryDefaults, setCategoryDefaults] = useState({});
  const [qosStatus, setQosStatus] = useState([]);
  const [switchEvents, setSwitchEvents] = useState([]);
  const [knownClasses, setKnownClasses] = useState([]);
  // Per-class scheduling action ("spread" | "failover"), so applying a quality
  // ceiling to a bonding class can warn + switch it to directed mode.
  const [policyActions, setPolicyActions] = useState({});
  const confirm = useConfirm();

  /* ── Link failover thresholds (global health block — what actually drives
     failover on an anchored edge: GET/PUT /qos/health) ── */
  const [healthCfg, setHealthCfg] = useState(null);
  const [healthDraft, setHealthDraft] = useState(null);
  const [healthSaving, setHealthSaving] = useState(false);
  const [showHealth, setShowHealth] = useState(false);

  /* ── UI ── */
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedClass, setSelectedClass] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [showEvents, setShowEvents] = useState(false);
  const [showInherited, setShowInherited] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  /* ── Fetch ── */
  const fetchJson = useCallback(async (path) => {
    const url = nodeApiUrl(path);
    if (!url) return null;
    const resp = await fetchWithTimeout(url);
    if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
    return resp.json();
  }, [nodeApiUrl]);

  const loadAll = useCallback(async () => {
    if (!selectedNode) return;
    try {
      setError(null);
      const [tData, pData, sData, eData, hData] = await Promise.all([
        fetchJson("/qos/thresholds"), fetchJson("/qos/presets"),
        fetchJson("/qos/status"), fetchJson("/qos/events"),
        fetchJson("/qos/health"),
      ]);
      if (tData?.thresholds) setThresholds(tData.thresholds);
      if (pData?.presets) setPresets(pData.presets);
      if (pData?.category_defaults) setCategoryDefaults(pData.category_defaults);
      if (pData?.known_classes) setKnownClasses(pData.known_classes);
      if (sData?.status) setQosStatus(sData.status);
      if (eData?.events) setSwitchEvents(eData.events);
      if (hData) setHealthCfg(hData);
      // Best-effort: legacy edges may omit /policy/action.
      try {
        const aData = await fetchJson("/policy/action");
        if (aData?.actions) setPolicyActions(aData.actions);
      } catch { /* no /policy/action on this edge */ }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [selectedNode, fetchJson]);

  useEffect(() => { setLoading(true); loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!selectedNode) return;
    const iv = setInterval(async () => {
      try {
        const [sData, eData] = await Promise.all([fetchJson("/qos/status"), fetchJson("/qos/events")]);
        if (sData?.status) setQosStatus(sData.status);
        if (eData?.events) setSwitchEvents(eData.events);
      } catch { /* transient */ }
    }, 5000);
    return () => clearInterval(iv);
  }, [selectedNode, fetchJson]);

  /* ── Toast ── */
  const flash = useCallback((msg, color) => {
    setToast({ msg, color });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2500);
  }, []);

  /* ── Save / Delete ── */
  // A class is bonding unless it carries an explicit "failover" action.
  // "spread"/"stripe" are the retired mode names for the same bonding action.
  const isSpreading = useCallback(
    (className) => ["bond", "spread", "stripe"].includes(policyActions[className] ?? "bond"),
    [policyActions]
  );

  // Force a class into directed (failover) mode via PUT /policy/action. Used
  // when a quality ceiling is applied to a class that's currently bonding —
  // a ceiling directs traffic, which is incompatible with order-blind spread.
  const setDirected = useCallback(async (className) => {
    const url = nodeApiUrl("/policy/action");
    if (!url) return;
    await fetchWithTimeout(url, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ class: className, action: "failover" }),
    });
    setPolicyActions((prev) => ({ ...prev, [className]: "failover" }));
  }, [nodeApiUrl]);

  const saveThreshold = useCallback(async (t) => {
    // A quality ceiling directs traffic and can't coexist with order-blind
    // bonding. If this class is currently spreading, confirm the switch to
    // directed mode, then force it explicitly (AutoFailoverForShaping is a
    // no-op for an explicitly-chosen spread, so we set it here directly).
    if (t?.enabled && isSpreading(t.class)) {
      const ok = await confirm({
        title: "Switch to directed mode?",
        message: `${friendlyClass(t.class)} is bonding (spread) across all healthy connections. A quality ceiling directs traffic to specific connections, so applying it will switch ${friendlyClass(t.class)} to directed mode (one connection at a time, with failover). Bonding will turn off for this class.`,
        confirmLabel: "Switch & apply",
        variant: "primary",
      });
      if (!ok) return;
      await setDirected(t.class);
    }
    setSaving(true);
    try {
      const url = nodeApiUrl("/qos/thresholds");
      if (!url) return;
      const resp = await fetchWithTimeout(url, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(t) });
      if (!resp.ok) throw new Error(await resp.text());
      await loadAll();
      setEditDraft(null);
      flash("Saved", "#22c55e");
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }, [nodeApiUrl, loadAll, flash, confirm, isSpreading, setDirected]);

  const applyPreset = useCallback(async (className, presetName) => {
    const preset = presets.find((p) => p.name === presetName);
    if (!preset) return;
    await saveThreshold({
      class: className, profile: preset.profile, min_score: preset.min_score,
      max_latency_ms: preset.max_latency_ms, max_jitter_ms: preset.max_jitter_ms,
      max_loss: preset.max_loss, min_bw_mbps: preset.min_bw_mbps,
      preset: presetName, enabled: presetName !== "best-effort",
    });
  }, [presets, saveThreshold]);

  const deleteThreshold = useCallback(async (className) => {
    setSaving(true);
    try {
      const url = nodeApiUrl(`/qos/thresholds?class=${encodeURIComponent(className)}`);
      if (!url) return;
      const resp = await fetchWithTimeout(url, { method: "DELETE" });
      if (!resp.ok) throw new Error(await resp.text());
      await loadAll();
      setEditDraft(null);
      flash("Reverted to category default", "#6366f1");
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }, [nodeApiUrl, loadAll, flash]);

  const handleLevelChange = useCallback(async (className, newLevel) => {
    const meta = LEVEL_META[newLevel];
    flash(`${friendlyClass(className)} → ${meta?.label || newLevel}`, meta?.color);
    await applyPreset(className, newLevel);
  }, [applyPreset, flash]);

  /* ── Failover thresholds (global health block) ── */
  const saveHealth = useCallback(async (cfg) => {
    setHealthSaving(true);
    try {
      const url = nodeApiUrl("/qos/health");
      if (!url) return;
      const resp = await fetchWithTimeout(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cfg),
      });
      if (!resp.ok) throw new Error(await resp.text());
      const updated = await resp.json();
      setHealthCfg(updated);
      setHealthDraft(null);
      flash("Failover thresholds applied", "#22c55e");
    } catch (err) { setError(err.message); }
    finally { setHealthSaving(false); }
  }, [nodeApiUrl, flash]);

  /* ── Merge all classes ── */
  const allClasses = useMemo(() => {
    const map = {};
    for (const t of thresholds) map[t.class] = { ...t, _inherited: false };
    for (const [cls, preset] of Object.entries(categoryDefaults)) {
      if (!map[cls]) {
        const p = presets.find((pr) => pr.name === preset);
        map[cls] = {
          class: cls, profile: p?.profile || "balanced", min_score: p?.min_score || 0,
          max_latency_ms: p?.max_latency_ms || 0, max_jitter_ms: p?.max_jitter_ms || 0,
          max_loss: p?.max_loss || 0, min_bw_mbps: p?.min_bw_mbps || 0,
          preset, enabled: preset !== "best-effort", _default: true, _inherited: false,
        };
      }
    }
    for (const kc of knownClasses) {
      if (map[kc.name]) continue;
      if (!kc.is_app) continue;
      const catThreshold = map[kc.category];
      if (catThreshold) {
        map[kc.name] = { ...catThreshold, class: kc.name, _inherited: true, _parentCategory: kc.category };
      } else {
        map[kc.name] = {
          class: kc.name, profile: "balanced", min_score: 0, max_latency_ms: 0,
          max_jitter_ms: 0, max_loss: 0, min_bw_mbps: 0,
          preset: "best-effort", enabled: false, _inherited: true, _parentCategory: kc.category || "BE",
        };
      }
    }
    return Object.values(map);
  }, [thresholds, categoryDefaults, presets, knownClasses]);

  /* ── Filter + sort for the flat list ── */
  const visibleClasses = useMemo(() => {
    const q = search.toLowerCase();
    const rows = allClasses.filter((row) => {
      if (row._inherited && !showInherited) return false;
      if (q && !friendlyClass(row.class).toLowerCase().includes(q) && !row.class.toLowerCase().includes(q)) return false;
      return true;
    });
    rows.sort((a, b) => {
      const aApp = a.class.startsWith("APP_") ? 1 : 0;
      const bApp = b.class.startsWith("APP_") ? 1 : 0;
      if (aApp !== bApp) return aApp - bApp;            // categories first
      const la = LEVEL_ORDER.indexOf(inferLevel(a));
      const lb = LEVEL_ORDER.indexOf(inferLevel(b));
      if (la !== lb) return la - lb;                    // most demanding first
      return friendlyClass(a.class).localeCompare(friendlyClass(b.class));
    });
    return rows;
  }, [allClasses, search, showInherited]);

  const categoryRows = useMemo(() => visibleClasses.filter((r) => !r.class.startsWith("APP_")), [visibleClasses]);
  const appRows = useMemo(() => visibleClasses.filter((r) => r.class.startsWith("APP_")), [visibleClasses]);

  const statusMap = useMemo(() => { const m = {}; for (const s of qosStatus) m[s.class] = s; return m; }, [qosStatus]);
  const inheritedCount = useMemo(() => allClasses.filter((r) => r._inherited).length, [allClasses]);

  /* ── Wheels: open balanced, and centre the orb picked ── */
  const catWheelRef = useWheel(selectedClass, categoryRows.map((r) => r.class).join(" "));
  const appWheelRef = useWheel(selectedClass, appRows.map((r) => r.class).join(" "));
  const nudge = (ref, dir) => { if (ref.current) ref.current.scrollBy({ left: dir * 240, behavior: "smooth" }); };

  const selectedRow = useMemo(
    () => visibleClasses.find((r) => r.class === selectedClass) || null,
    [visibleClasses, selectedClass]
  );

  /* ══════════════════════════════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════════════════════════════ */

  if (!selectedNode) {
    return (
      <div className="qz-wrap">
        <div className="qz-blank"><FiSliders className="qz-blank-ico" /><h3>Select a node</h3><p>Choose an edge node to set connection-quality rules.</p></div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="qz-wrap">
        <div className="qz-loading"><FiRefreshCw className="qz-spin" /> Loading quality rules…</div>
      </div>
    );
  }

  const renderOrb = (row) => {
    const isSel = selectedClass === row.class;
    const level = inferLevel(row);
    const meta = LEVEL_META[level];
    const status = statusMap[row.class];
    const Icon = meta.icon;
    return (
      <button
        key={row.class}
        data-orb={row.class}
        type="button"
        className={`qz-orb${isSel ? " qz-orb--on" : ""}`}
        style={{ "--qz-accent": meta.color }}
        onClick={() => { setSelectedClass(isSel ? null : row.class); setEditDraft(null); }}
        title={friendlyClass(row.class)}
      >
        <span className="qz-orb-ring">
          <span className="qz-orb-ico"><Icon size={20} /></span>
          {row._inherited && <span className="qz-orb-inh-dot" title="inherited" />}
        </span>
        <span className="qz-orb-name">{friendlyClass(row.class)}</span>
        <span className="qz-orb-level">{meta.label}</span>
        {status?.wans && (
          <span className="qz-orb-dots">
            {status.wans.map((ws) => (
              <span key={ws.wan_name} className={`qz-dot${ws.meets ? " ok" : " fail"}`} title={`${wanName(ws.wan_name)}: ${ws.meets ? "meets quality" : ws.fail_reason}`} />
            ))}
          </span>
        )}
      </button>
    );
  };

  const renderBody = (row) => {
    const isApp = row.class.startsWith("APP_");
    const level = inferLevel(row);
    const meta = LEVEL_META[level];
    const status = statusMap[row.class];
    const isEditing = editDraft !== null;

    return (
      <div className="qz-bloom-body">
        {/* What this means */}
        <p className="qz-meaning">{meta.long}</p>

        {/* Current bonding mode — makes it obvious that picking a quality level
            will take this class out of bonding and into directed mode. */}
        <div
          style={{
            display: "flex", alignItems: "center", gap: 8, margin: "0 0 10px",
            padding: "6px 10px", borderRadius: 8, fontSize: "0.76rem", fontWeight: 600,
            border: `1px solid ${isSpreading(row.class) ? "#bbf7d0" : "#e2e8f0"}`,
            background: isSpreading(row.class) ? "#f0fdf4" : "#f8fafc",
            color: isSpreading(row.class) ? "#166534" : "#475569",
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: isSpreading(row.class) ? "#16a34a" : "#94a3b8" }} />
          {isSpreading(row.class)
            ? <>Bonding — spread across all healthy connections. Choosing a quality level will switch this to directed mode.</>
            : <>Directed — follows connections one at a time with failover; quality levels apply here.</>}
        </div>

            {/* Level picker */}
            <div className="qz-levels">
              {LEVEL_ORDER.map((lvl) => {
                const m = LEVEL_META[lvl];
                const LIcon = m.icon;
                const on = level === lvl;
                return (
                  <button
                    key={lvl}
                    type="button"
                    className={`qz-level${on ? " qz-level--on" : ""}`}
                    style={{ "--lc": m.color }}
                    disabled={saving}
                    onClick={() => handleLevelChange(row.class, lvl)}
                  >
                    <span className="qz-level-ico"><LIcon size={15} /></span>
                    <span className="qz-level-txt">
                      <span className="qz-level-label">{m.label}</span>
                      <span className="qz-level-short">{m.short}</span>
                    </span>
                    {on && <FiCheck size={14} className="qz-level-check" />}
                  </button>
                );
              })}
            </div>

            {/* Live WAN quality */}
            {status?.wans?.length > 0 && (
              <div className="qz-wanq">
                <div className="qz-wanq-title">How each connection rates for this traffic now — links that fail are moved to the back</div>
                <div className="qz-wanq-list">
                  {status.wans.map((ws) => (
                    <div key={ws.wan_name} className={`qz-wanq-row${ws.meets ? "" : " fail"}`}>
                      <FiWifi size={13} />
                      <span className="qz-wanq-name"><WanLabel iface={ws.wan_name} /></span>
                      <span className="qz-wanq-bar"><span className="qz-wanq-fill" style={{ width: `${Math.max(0, Math.min(100, ws.score || 0))}%`, background: scoreColor(ws.score) }} /></span>
                      <span className="qz-wanq-score" style={{ color: scoreColor(ws.score) }}>{ws.score?.toFixed(0)}</span>
                      {ws.meets
                        ? <span className="qz-wanq-badge ok"><FiCheck size={10} /> good</span>
                        : <span className="qz-wanq-badge fail" title={ws.fail_reason}><FiX size={10} /> {ws.fail_reason}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {row._inherited && (
              <div className="qz-inherit-note">
                <FiInfo size={12} /> Following the <strong>{friendlyClass(row._parentCategory)}</strong> category. Pick a level above to give {friendlyClass(row.class)} its own rule.
              </div>
            )}

            {/* Advanced (raw thresholds) */}
            {!isEditing ? (
              <div className="qz-card-actions">
                <button className="qz-link-btn" onClick={() => setEditDraft({
                  class: row.class, profile: row.profile || "balanced",
                  min_score: row.min_score || 0, max_latency_ms: row.max_latency_ms || 0,
                  max_jitter_ms: row.max_jitter_ms || 0, max_loss: row.max_loss || 0,
                  min_bw_mbps: row.min_bw_mbps || 0, _lossPct: (row.max_loss || 0) * 100,
                  preset: "", enabled: true,
                })}>
                  <FiSliders size={12} /> Advanced thresholds
                </button>
                {isApp && !row._inherited && (
                  <button className="qz-link-btn danger" disabled={saving} onClick={() => deleteThreshold(row.class)}>
                    <FiX size={12} /> Revert to category
                  </button>
                )}
              </div>
            ) : (
              <div className="qz-adv">
                <div className="qz-adv-title">Advanced thresholds — switch away from a connection when it breaches any of these:</div>
                <div className="qz-adv-grid">
                  <label className="qz-field">
                    <span>Optimize for</span>
                    <select value={editDraft.profile} onChange={(e) => setEditDraft({ ...editDraft, profile: e.target.value })}>
                      <option value="low-latency">Low delay</option>
                      <option value="high-bw">High speed</option>
                      <option value="balanced">Balanced</option>
                    </select>
                  </label>
                  <label className="qz-field">
                    <span>Min quality score (0–100)</span>
                    <input type="number" min="0" max="100" value={editDraft.min_score} onChange={(e) => setEditDraft({ ...editDraft, min_score: +e.target.value })} />
                  </label>
                  <label className="qz-field">
                    <span>Max delay (ms)</span>
                    <input type="number" min="0" value={editDraft.max_latency_ms} onChange={(e) => setEditDraft({ ...editDraft, max_latency_ms: +e.target.value })} />
                  </label>
                  <label className="qz-field">
                    <span>Max jitter (ms)</span>
                    <input type="number" min="0" value={editDraft.max_jitter_ms} onChange={(e) => setEditDraft({ ...editDraft, max_jitter_ms: +e.target.value })} />
                  </label>
                  <label className="qz-field">
                    <span>Max packet loss (%)</span>
                    <input type="number" step="0.1" min="0" max="100" value={editDraft._lossPct ?? 0} onChange={(e) => setEditDraft({ ...editDraft, _lossPct: +e.target.value, max_loss: +e.target.value / 100 })} />
                  </label>
                  <label className="qz-field">
                    <span>Min speed (Mbps)</span>
                    <input type="number" min="0" value={editDraft.min_bw_mbps} onChange={(e) => setEditDraft({ ...editDraft, min_bw_mbps: +e.target.value })} />
                  </label>
                </div>
                <div className="qz-adv-foot">
                  <button className={`qz-switch${editDraft.enabled ? " on" : ""}`} onClick={() => setEditDraft({ ...editDraft, enabled: !editDraft.enabled })}>
                    {editDraft.enabled ? "Rule active" : "Rule off"}
                  </button>
                  <div className="qz-adv-btns">
                    <button className="qz-btn" onClick={() => setEditDraft(null)}>Cancel</button>
                    <button className="qz-btn primary" disabled={saving} onClick={() => { const { _lossPct, ...rest } = editDraft; saveThreshold(rest); }}>
                      <FiSave size={12} /> {saving ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            )}
      </div>
    );
  };

  return (
    <div className="qz-wrap">
      {error && <div className="qz-error"><FiAlertTriangle size={14} /> {error}</div>}
      {toast && <div className="qz-toast" style={{ borderLeftColor: toast.color || "#6366f1" }}>{toast.msg}</div>}

      {/* Explain strip */}
      <div className="qz-explain">
        <FiInfo size={15} className="qz-explain-ico" />
        <div>
          <strong>Set how sensitive each traffic type is to connection quality.</strong> When a
          connection's delay, jitter or loss crosses a class's limits, Weavian pushes that link to
          the back of that class's order and moves the traffic to a healthier connection — before
          the link actually fails. The <strong>Failover</strong> panel sets the harder limit at
          which a link is dropped for <em>all</em> traffic.
        </div>
      </div>

      {/* Toolbar */}
      <div className="qz-toolbar">
        <div className="qz-search">
          <FiSearch size={14} className="qz-search-ico" />
          <input className="qz-search-input" placeholder="Find a category or app…" value={search} onChange={(e) => setSearch(e.target.value)} />
          {search && <button className="qz-search-clear" onClick={() => setSearch("")}><FiX size={13} /></button>}
        </div>
        <div className="qz-tools">
          {inheritedCount > 0 && (
            <button className={`qz-tool${showInherited ? " on" : ""}`} onClick={() => setShowInherited((v) => !v)} title={showInherited ? "Hide inherited apps" : "Show inherited apps"}>
              {showInherited ? <FiEyeOff size={13} /> : <FiEye size={13} />} <span>Inherited</span>
            </button>
          )}
          <button
            className={`qz-tool${showHealth ? " on" : ""}`}
            onClick={() => { setShowHealth((v) => !v); setHealthDraft(null); }}
            title="Global link-failover thresholds"
          >
            <FiShield size={13} /> <span>Failover</span>
          </button>
          <button className={`qz-tool${showEvents ? " on" : ""}`} onClick={() => setShowEvents((v) => !v)} title="Recent switches">
            <FiClock size={13} /> <span>Activity</span>
            {switchEvents.length > 0 && <span className="qz-tool-badge" />}
          </button>
          <button className="qz-tool" onClick={loadAll} title="Refresh"><FiRefreshCw size={13} /></button>
        </div>
      </div>

      {/* Failover thresholds (global health block — the real failover control) */}
      {showHealth && healthCfg && (() => {
        const d = healthDraft || {
          loss_pct: Math.round((healthCfg.loss_threshold || 0) * 1000) / 10,
          rtt_threshold_ms: healthCfg.rtt_threshold_ms || 0,
          download_floor_pct: Math.round((healthCfg.download_delivery_floor || 0) * 1000) / 10,
          sustained_s: Math.round(((healthCfg.sustained_window || 0) / 1e9) * 10) / 10,
          recovery_s: Math.round(((healthCfg.recovery_window || 0) / 1e9) * 10) / 10,
          missed_probes_fast_degrade: healthCfg.missed_probes_fast_degrade ?? 0,
          missed_acks_fast_recover: healthCfg.missed_acks_fast_recover ?? 0,
        };
        const set = (k, v) => setHealthDraft({ ...d, [k]: v });
        const onSave = () => saveHealth({
          loss_threshold: Math.max(0, d.loss_pct) / 100,
          rtt_threshold_ms: Math.max(0, d.rtt_threshold_ms),
          download_delivery_floor: Math.max(0, d.download_floor_pct) / 100,
          sustained_window: Math.round(Math.max(0, d.sustained_s) * 1e9),
          recovery_window: Math.round(Math.max(0, d.recovery_s) * 1e9),
          missed_probes_fast_degrade: Math.max(0, Math.round(d.missed_probes_fast_degrade)),
          missed_acks_fast_recover: Math.max(0, Math.round(d.missed_acks_fast_recover)),
          per_wan: healthCfg.per_wan,
        });
        return (
          <div className="qz-events qz-health">
            <div className="qz-events-title"><FiShield size={13} /> When does a link get abandoned? (applies to ALL connections)</div>
            <div className="qz-health-note">
              <FiInfo size={12} /> These are the thresholds the edge actually uses to mark a WAN
              degraded and reroute traffic to a healthy link. A link breaching any signal for the
              sustained window is dropped; it must stay clean for the recovery window to come back.
              Changes apply live — no restart.
            </div>
            <div className="qz-adv-grid">
              <label className="qz-field">
                <span>Max packet loss (%)</span>
                <input type="number" step="0.1" min="0" max="100" value={d.loss_pct}
                  onChange={(e) => set("loss_pct", +e.target.value)} />
              </label>
              <label className="qz-field">
                <span>Max RTT (ms)</span>
                <input type="number" min="0" value={d.rtt_threshold_ms}
                  onChange={(e) => set("rtt_threshold_ms", +e.target.value)} />
              </label>
              <label className="qz-field">
                <span>Min download delivery (%)</span>
                <input type="number" step="1" min="0" max="100" value={d.download_floor_pct}
                  onChange={(e) => set("download_floor_pct", +e.target.value)} />
              </label>
              <label className="qz-field">
                <span>Sustained breach before drop (s)</span>
                <input type="number" step="0.5" min="0" value={d.sustained_s}
                  onChange={(e) => set("sustained_s", +e.target.value)} />
              </label>
              <label className="qz-field">
                <span>Clean window before recovery (s)</span>
                <input type="number" step="0.5" min="0" value={d.recovery_s}
                  onChange={(e) => set("recovery_s", +e.target.value)} />
              </label>
              <label className="qz-field">
                <span>Missed probes → fast drop</span>
                <input type="number" step="1" min="0" value={d.missed_probes_fast_degrade}
                  onChange={(e) => set("missed_probes_fast_degrade", +e.target.value)} />
              </label>
              <label className="qz-field">
                <span>Good probes → fast recover</span>
                <input type="number" step="1" min="0" value={d.missed_acks_fast_recover}
                  onChange={(e) => set("missed_acks_fast_recover", +e.target.value)} />
              </label>
            </div>
            <div className="qz-health-hint">0 disables a signal. Loss/RTT/delivery of 0 turns that check off; the missed-probe fast path still applies.</div>
            <div className="qz-adv-foot">
              <div />
              <div className="qz-adv-btns">
                {healthDraft && <button className="qz-btn" onClick={() => setHealthDraft(null)}>Reset</button>}
                <button className="qz-btn primary" disabled={healthSaving} onClick={onSave}>
                  <FiSave size={12} /> {healthSaving ? "Applying…" : "Apply to all links"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Activity / events */}
      {showEvents && (
        <div className="qz-events">
          <div className="qz-events-title">Recent failover activity</div>
          {switchEvents.length === 0 ? (
            <div className="qz-events-empty">No failovers yet — every link is meeting its targets.</div>
          ) : (
            <div className="qz-events-list">
              {switchEvents.slice(-14).reverse().map((evt, i) => {
                const warn = evt.level === "warn" || evt.level === "error";
                return (
                  <div key={i} className="qz-event">
                    {warn
                      ? <FiAlertTriangle size={12} style={{ color: "#f59e0b", flexShrink: 0 }} />
                      : <FiCheck size={12} style={{ color: "#22c55e", flexShrink: 0 }} />}
                    <span className="qz-event-msg">{evt.message}</span>
                    <span className="qz-event-time">{evt.time ? new Date(evt.time).toLocaleTimeString() : ""}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Wheels */}
      <div className="qz-wheels">
        {categoryRows.length > 0 && (
          <div className="qz-wheelsec">
            <div className="qz-wheel-lbl">Traffic categories</div>
            <div className="qz-wheel-row">
              <button type="button" className="qz-wheel-nav" onClick={() => nudge(catWheelRef, -1)} aria-label="Scroll left"><FiChevronDown size={15} style={{ transform: "rotate(90deg)" }} /></button>
              <div className="qz-wheel" ref={catWheelRef}>{categoryRows.map(renderOrb)}</div>
              <button type="button" className="qz-wheel-nav" onClick={() => nudge(catWheelRef, 1)} aria-label="Scroll right"><FiChevronDown size={15} style={{ transform: "rotate(-90deg)" }} /></button>
            </div>
          </div>
        )}
        {appRows.length > 0 && (
          <div className="qz-wheelsec">
            <div className="qz-wheel-lbl">Applications</div>
            <div className="qz-wheel-row">
              <button type="button" className="qz-wheel-nav" onClick={() => nudge(appWheelRef, -1)} aria-label="Scroll left"><FiChevronDown size={15} style={{ transform: "rotate(90deg)" }} /></button>
              <div className="qz-wheel" ref={appWheelRef}>{appRows.map(renderOrb)}</div>
              <button type="button" className="qz-wheel-nav" onClick={() => nudge(appWheelRef, 1)} aria-label="Scroll right"><FiChevronDown size={15} style={{ transform: "rotate(-90deg)" }} /></button>
            </div>
          </div>
        )}
        {visibleClasses.length === 0 && (
          <div className="qz-list-empty"><FiSearch size={22} style={{ opacity: 0.25 }} /><span>Nothing matches “{search}”.</span></div>
        )}
      </div>

      {/* Blooming stage */}
      <div className={`qz-stage${selectedRow ? " qz-stage--open" : ""}`}>
        {selectedRow ? (() => {
          const meta = LEVEL_META[inferLevel(selectedRow)];
          const Icon = meta.icon;
          return (
            <div className="qz-bloom" style={{ "--qz-accent": meta.color }}>
              <div className="qz-bloom-head">
                <span className="qz-bloom-ico" style={{ background: `color-mix(in srgb, ${meta.color} 16%, transparent)`, color: meta.color }}><Icon size={20} /></span>
                <div className="qz-bloom-titles">
                  <span className="qz-bloom-title">{friendlyClass(selectedRow.class)}</span>
                  <span className="qz-bloom-sub">{meta.label} · {meta.short}</span>
                </div>
                <button type="button" className="qz-bloom-close" onClick={() => { setSelectedClass(null); setEditDraft(null); }} aria-label="Close"><FiX size={16} /></button>
              </div>
              {renderBody(selectedRow)}
            </div>
          );
        })() : (
          <div className="qz-stage-hint"><FiSliders size={16} /><span>Pick a traffic type on the wheel to set how sensitive it is to connection quality.</span></div>
        )}
      </div>
    </div>
  );
}
